import type { FeedbackType } from "@asurion-core/expert-workspace-feedback";
import type { AgentActivity, AudioType, GaiaPayloadConfigs, Partner } from "@expert/shared-types";
import type { GuideAppConfig } from "@expert/solve-tools";

export interface AmazonConnectInstanceInfo {
    region: string;
    instanceId: string;
    instanceAlias: string;
    secondaryInstanceAlias?: string;
    identityProvider?: { loginUrl: string; searchParamName: string };
}

export type AmazonConnectInstancesInfo = Record<Partner, AmazonConnectInstanceInfo>;

interface PartnerProgram {
    name: string;
    claimEligible: boolean;
    survey: {
        /** @deprecated This field is no longer used and will be removed in the future. */
        generalSurveyId?: string;
        /** @deprecated This field is no longer used and will be removed in the future. */
        claimsSurveyId?: string;
    };
}

/**
 * Config options for features enabled by an {@link AgentActivity} that signals
 * technical issues.
 */
export interface TechnicalIssuesConfig {
    /** The name of the dedicated {@link AgentActivity} for technical issues. */
    agentActivity: AgentActivity;
    /**
     * Enables automatically setting the agent's activity to the specified
     * {@link TechnicalIssuesConfig.agentActivity | agentActivity} whenever
     * technical issues are encountered (audio disconnect).
     */
    autoSet?: boolean;
    /**
     * Enables outbound calls whenever the agent's activity is set to the
     * specified {@link TechnicalIssuesConfig.agentActivity | agentActivity}
     * even if the partner configuration does not have outbound calls enabled.
     */
    enableOutboundCalls?: boolean;
}

type SessionSummaryStates = "autosend" | "manualCopy" | "maximum";
export type sessionSummaryTimesConfig = Record<SessionSummaryStates, number>;

interface Features {
    callbackLater: boolean;
    callbackEnabled: boolean;
    outboundCall: boolean;
    surveyEnabled: boolean;
    groupingChatQueueEnabled: boolean;
    postCallNotepadRedaction: boolean;
    customerPanelEnabled: boolean;
    isEndCallResolutionMenuEnabled: boolean;
    isExpertPerformanceDashboardEnabled: boolean;
    isRealtimeTranscriptEnabled: boolean;
    logOutOnOfflineAgentActivity: boolean;
    callControlsEnabled: boolean;
    transferListEnabled: boolean;
    medialess?: boolean;
    isUnifiedSalesViewEnabled: boolean;
    csdEnabled: boolean;
    feedback: boolean;
    feedbackType: FeedbackType;
    isDigitalCompetencyInsightEnabled: boolean;
    simplifiedSummary: boolean;
    ozmo: boolean;
    disableAgentStatusChange: boolean;
    verificationType: "phoneNumber" | "billingAccountNumberAndPin";
    isCustomerDetailsV2Enabled?: boolean;
    programSelectionEnabled?: boolean;
    isResolutionStatusFormEnabled?: boolean;
    isOfflineAgentActivitySelectable?: boolean;
    cleanMissedCalls?: boolean;
    isSalesQuestionnaireEnabled?: boolean;
    isChallengesEnabled: boolean;
    isExpertUniversityEnabled: boolean;
    /** Toggles between react-international-phone and MaskedInput for MdnInput.tsx. */
    isInternationalPhoneEnabled?: boolean;
    /** Toggles between legacy transfer menu and new transfer menu that supports international phone inputs. */
    isSegmentedTransferMenuEnabled?: boolean;
    /** Default country code for phone inputs e.g "US" which notifies UI to use "+1". */
    defaultCountry?: string;
    /** Countries enabled for outbound dialing. */
    clientEnabledCountries?: string[];
    /** Enables legacy VDN input for transfers. */
    isVdnTransferInputEnabled?: boolean;
    /** Allows user to select between multiple countries for phone inputs. */
    countrySelectEnabled?: boolean;
    isRealTimeComplianceCheckEnabled?: boolean;
}

type AdditionalVerificationField = "email" | "orderId" | "accountNumber";

export const leaderboardMetrics = Object.freeze(["calls", "sales"] as const);
export type LeaderboardMetric = (typeof leaderboardMetrics)[number];
export function isLeaderboardMetric(value: unknown): value is LeaderboardMetric {
    return (leaderboardMetrics as readonly unknown[]).includes(value);
}
export interface LeaderboardConfig {
    controlText: string;
    description: string;
    metric: LeaderboardMetric;
    reminder: string;
    sku?: string;
}

interface PerformanceMetricConfig {
    body: string;
    header: string;
    metric:
        | "totalCalls"
        | "callResolutionTime"
        | "oneCallResolution"
        | "resolutionRate"
        | "transferRate"
        | "perfectSurveys"
        | "fiveStarRating"
        | "entireRepPerformance"
        | "salesPer100"
        | "cancellationRate"
        | "netEnrolls";
}

export interface HubMetricConfig {
    id: string;
    title: string;
    source: HubMetricSource;
    /** Goal shown under the value. Omit for a card with no target (e.g. Total calls). */
    target?: HubMetricTarget;
    /** Show target with status pill. */
    grade?: boolean;
    /** Fraction digits when formatting the value (rating and percent ratings use 1; counts use 0). */
    decimals?: number;
    emptyText?: string;
}

export type HubMetricSource =
    | { kind: "dataverse"; bucket: "serve" | "solve" | "sell"; key: string }
    | { kind: "gamification"; metric: "totalCalls" | "sales" | "pacing" };

export interface HubMetricTarget {
    value: number;
    /** Which direction is good — drives card tone/status. */
    comparison: "higher" | "lower";
    unit: "percent" | "rating" | "count";
    /** Singular count noun for count-unit footers ("10 sales" / "1 sale"); pluralized in the card. */
    noun?: string;
}

export interface PartnerConfig {
    features: Features;
    activationSupportNumbers: string[];
    activationSupportQueues?: string[];
    additionalVerificationFields?: AdditionalVerificationField[];
    programs: PartnerProgram[];
    requiresManualCopy: boolean;
    extraSessionSummaryPayloadProps?: Record<string, string>;
    accountNumberLength?: number;
    allowedAccountNumberLengths?: number[];
    partnerTool: string;
    partnerToolUrl?: string;
    performanceMetrics: PerformanceMetricConfig[];
    hubMetrics?: HubMetricConfig[];
    orderIdMask?: string;
    /** Options enabled by a dedicated {@link AgentActivity} for technical issues. */
    technicalIssues?: TechnicalIssuesConfig;
    /** The {@link AgentActivity} to set while waiting to perform a callback. */
    callbackPendingAgentActivity?: AgentActivity;
    trainingAgentActivity?: AgentActivity;
    wrappingAgentActivity?: AgentActivity;
    defaultAgentActivity?: AgentActivity;
    solveGuidedFlows?: GuideAppConfig;
    contactStateTypesToAutoClear?: string[];
    gaiaSummaryModel: string;
    maxSummaryGenerationTime?: number;
    sessionSummaryTimes?: sessionSummaryTimesConfig;
    blocklistedPrefixes?: string[];
    blocklistedConfirmationCodes?: string[];
    blocklistedNumbers?: string[];
    /**
     * Well-formed numbers that can never belong to a subscriber, so a session summary carrying one is
     * never forwarded to the partner notes API. Distinct from `blocklistedNumbers`, which also blanks
     * the MDN shown in the customer panel.
     */
    blocklistedSubscriberNumbers?: string[];
    survey?: SurveyConfig;
    gaiaPayloadProps: GaiaPayloadConfigs;
    sessionSummary?: SessionSummaryConfig;
    salesQuestionnaireProps?: SalesQuestionnaire;
    leaderboards: LeaderboardConfig[];
    pacingTargets?: PacingTargetsConfig;
    partnerEventWhitelist?: string[];
    amazonConnectInstancesInfoConfigKey?: string;
    /**
     * Pre-recorded-disclosure Amazon Connect Quick Connect (transfer-destination) ARNs, keyed by
     * AudioType (English / Spanish). Env-scoped by the served AppConfig profile. Only partners with
     * this populated (today: att) get an audio disclosure — see {@link getDisclosureEndpointArn}.
     */
    disclosureQuickConnectArns?: Record<AudioType, string>;
}

export interface PacingTargetsConfig {
    enabled: boolean;
    defaultTargetSeconds: number;
    salesEligibleBufferSeconds: number;
    callTypeTargets: Record<string, number>;
    approachingThreshold: number;
    exceededThreshold: number;
}

export type PartnerAppConfig = Record<Partner, PartnerConfig>;

interface QueryBySearchTypeConfig {
    clientChannelId: string;
    query: string;
}

export interface PartnerSearchConfig {
    queriesBySearchType: { phoneNumber?: QueryBySearchTypeConfig[] };
}

export type PartnerSearchAppConfig = Record<Partner, PartnerSearchConfig>;

export interface AppConfig {
    amazonConnectInstancesInfo?: AmazonConnectInstancesInfo;
    partnersConfig: PartnerAppConfig;
    partnerSearch?: PartnerSearchAppConfig;
    allowedPartners: string;
}

export interface AppConfigSingleton {
    amazonConnectInstancesInfo?: AmazonConnectInstancesInfo;
    getPartnersConfig: (partner: Partner) => PartnerConfig;
    getPartnerSearch?: (partner: Partner) => PartnerSearchConfig;
    allowedPartners: string[];
    hasPartnerConfig: (partner: Partner) => boolean;
}

export type CallReason = { reason: string; subReasons?: string[] };
export interface SessionSummaryConfig {
    callReasons?: CallReason[];
    dispositions?: string[];
    // TODO: Add all session summary configs here for better organization
}

export interface SalesQuestionnaire {
    plans?: string[];
    affiliates?: string[];
}

export interface SurveyConfig {
    // @deprecated: This field is no longer used and will be removed in the future.
    claimsSurveyId?: string;
    // @deprecated: This field is no longer used and will be removed in the future.
    generalSurveyId?: string;
    isClaimsEligible: boolean;
    useCustomerAgreementProgram: boolean;
    ignoreProgram?: boolean;
    excludedPrograms?: string[];
    shouldSurveyNotResolved?: boolean;
    unresolvedSurveyId?: string;
}
